import { useState, useEffect } from 'react'
import { subscribeToProspects, createProspect, deleteProspect, updateProspect } from '../firebase/prospects'
import { subscribeToTags } from '../firebase/tags'
import './ProspectResearch.css'

const ProspectResearch = ({ user }) => {
  const [prospects, setProspects] = useState([])
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingProspect, setEditingProspect] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTagFilter, setSelectedTagFilter] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, prospectId: null, prospectName: '' })
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  useEffect(() => {
    if (!user) return

    // Subscribe to real-time prospects updates (shared resource)
    const unsubscribeProspects = subscribeToProspects((prospectsData) => {
      setProspects(prospectsData)
      setLoading(false)
    })

    // Subscribe to real-time tags updates (for tagging prospects)
    const unsubscribeTags = subscribeToTags(user.uid, (tagsData) => {
      setTags(tagsData.map(tag => tag.name))
    })

    return () => {
      unsubscribeProspects()
      unsubscribeTags()
    }
  }, [user])

  const handleCreateProspect = async (prospectData) => {
    try {
      await createProspect(prospectData)
      setShowForm(false)
      setEditingProspect(null)
    } catch (error) {
      console.error('Error creating prospect:', error)
      alert('Failed to create prospect. Please try again.')
    }
  }

  const handleUpdateProspect = async (prospectData) => {
    try {
      await updateProspect(editingProspect.id, prospectData)
      setShowForm(false)
      setEditingProspect(null)
    } catch (error) {
      console.error('Error updating prospect:', error)
      alert('Failed to update prospect. Please try again.')
    }
  }

  const handleEditProspect = (prospect) => {
    setEditingProspect(prospect)
    setShowForm(true)
  }

  const handleDeleteProspect = async (prospectId, e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    
    const prospect = prospects.find(p => p.id === prospectId)
    const prospectName = prospect?.name || 'this prospect'
    
    setDeleteConfirm({ show: true, prospectId, prospectName })
  }

  const confirmDelete = async () => {
    const { prospectId } = deleteConfirm
    
    try {
      await deleteProspect(prospectId)
      setDeleteConfirm({ show: false, prospectId: null, prospectName: '' })
      alert('✅ Prospect deleted successfully')
    } catch (error) {
      console.error('Error deleting prospect:', error)
      alert(`❌ Failed to delete prospect: ${error.message || 'Unknown error'}`)
      setDeleteConfirm({ show: false, prospectId: null, prospectName: '' })
    }
  }

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, prospectId: null, prospectName: '' })
  }

  const handleRemoveTag = async (prospectId, tagToRemove) => {
    try {
      const prospect = prospects.find(p => p.id === prospectId)
      if (!prospect) return

      const currentTags = prospect.tags || []
      const updatedTags = currentTags.filter(tag => tag !== tagToRemove)
      await updateProspect(prospectId, { tags: updatedTags })
    } catch (error) {
      console.error('Error removing tag:', error)
      alert('Failed to remove tag. Please try again.')
    }
  }

  const handleAddTag = async (prospectId, tagToAdd) => {
    try {
      const prospect = prospects.find(p => p.id === prospectId)
      if (!prospect) return

      const currentTags = prospect.tags || []
      if (!currentTags.includes(tagToAdd)) {
        const updatedTags = [...currentTags, tagToAdd]
        await updateProspect(prospectId, { tags: updatedTags })
      }
    } catch (error) {
      console.error('Error adding tag:', error)
      alert('Failed to add tag. Please try again.')
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return '-'
    
    let date
    if (timestamp.toDate) {
      date = timestamp.toDate()
    } else if (timestamp instanceof Date) {
      date = timestamp
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000)
    } else {
      return '-'
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Filter prospects
  const filteredProspects = prospects.filter((prospect) => {
    const searchLower = searchTerm.toLowerCase()
    const matchesSearch = !searchTerm || (
      prospect.name?.toLowerCase().includes(searchLower) ||
      prospect.role?.toLowerCase().includes(searchLower) ||
      prospect.company?.toLowerCase().includes(searchLower) ||
      prospect.country?.toLowerCase().includes(searchLower) ||
      prospect.industry?.toLowerCase().includes(searchLower) ||
      prospect.source?.toLowerCase().includes(searchLower)
    )
    
    const matchesTag = !selectedTagFilter || (prospect.tags || []).includes(selectedTagFilter)
    
    return matchesSearch && matchesTag
  })

  // Sort function
  const sortProspects = (prospectsToSort, column, direction) => {
    if (!column) return prospectsToSort

    return [...prospectsToSort].sort((a, b) => {
      let aValue = a[column]
      let bValue = b[column]

      if (column === 'createdAt' || column === 'updatedAt') {
        const getDate = (timestamp) => {
          if (!timestamp) return new Date(0)
          if (timestamp.toDate) return timestamp.toDate()
          if (timestamp instanceof Date) return timestamp
          if (timestamp.seconds) return new Date(timestamp.seconds * 1000)
          return new Date(0)
        }
        aValue = getDate(aValue)
        bValue = getDate(bValue)
      } else if (column === 'tags') {
        const aTags = aValue || []
        const bTags = bValue || []
        if (aTags.length !== bTags.length) {
          return aTags.length - bTags.length
        }
        aValue = aTags.length > 0 ? aTags[0] : ''
        bValue = bTags.length > 0 ? bTags[0] : ''
      } else {
        aValue = aValue || ''
        bValue = bValue || ''
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
      }

      let comparison = 0
      if (aValue < bValue) {
        comparison = -1
      } else if (aValue > bValue) {
        comparison = 1
      }

      return direction === 'asc' ? comparison : -comparison
    })
  }

  const sortedProspects = sortProspects(filteredProspects, sortColumn, sortDirection)

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  if (loading) {
    return <div className="loading">Loading prospects...</div>
  }

  return (
    <div className="prospect-research-page">
      <div className="page-header">
        <div>
          <h2>Prospect Research (Individuals)</h2>
          <p className="subtitle">Recommended: 7-10 individuals per day • {prospects.length} total individuals</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + New Prospect
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by name, role, company, country, industry, or source..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="tag-filter">
          <select
            value={selectedTagFilter}
            onChange={(e) => setSelectedTagFilter(e.target.value)}
            className="tag-filter-select"
          >
            <option value="">All Tags</option>
            {tags.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>
        </div>
      </div>

      {showForm && (
        <ProspectForm
          onSubmit={editingProspect ? handleUpdateProspect : handleCreateProspect}
          onCancel={() => {
            setShowForm(false)
            setEditingProspect(null)
          }}
          tags={tags}
          initialData={editingProspect}
        />
      )}

      {sortedProspects.length === 0 ? (
        <div className="empty-state">
          <p>{searchTerm || selectedTagFilter ? 'No prospects match your filters.' : 'No prospects yet. Add your first prospect!'}</p>
        </div>
      ) : (
        <div className="prospects-table-container">
          <table className="prospects-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('name')}>
                  <span>Name</span>
                  {sortColumn === 'name' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('role')}>
                  <span>Role</span>
                  {sortColumn === 'role' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('company')}>
                  <span>Company</span>
                  {sortColumn === 'company' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('country')}>
                  <span>Country</span>
                  {sortColumn === 'country' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('source')}>
                  <span>Source</span>
                  {sortColumn === 'source' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>LinkedIn URL</th>
                <th className="sortable" onClick={() => handleSort('companySize')}>
                  <span>Company Size</span>
                  {sortColumn === 'companySize' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('industry')}>
                  <span>Industry</span>
                  {sortColumn === 'industry' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('tags')}>
                  <span>Tags</span>
                  {sortColumn === 'tags' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('createdAt')}>
                  <span>Created</span>
                  {sortColumn === 'createdAt' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedProspects.map((prospect) => (
                <tr key={prospect.id}>
                  <td className="prospect-name-cell">
                    {prospect.name || '-'}
                  </td>
                  <td>{prospect.role || '-'}</td>
                  <td>{prospect.company || '-'}</td>
                  <td>{prospect.country || '-'}</td>
                  <td>{prospect.source || '-'}</td>
                  <td>
                    {prospect.linkedinUrl ? (
                      <a href={prospect.linkedinUrl} target="_blank" rel="noopener noreferrer">
                        View Profile
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{prospect.companySize || '-'}</td>
                  <td>{prospect.industry || '-'}</td>
                  <td className="prospect-tags-cell">
                    <div className="prospect-tags">
                      {(prospect.tags || []).map((tag, idx) => (
                        <span key={idx} className="tag">
                          {tag}
                          <button
                            className="tag-remove"
                            onClick={() => handleRemoveTag(prospect.id, tag)}
                            title="Remove tag"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <select
                      className="tag-add-select"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddTag(prospect.id, e.target.value)
                          e.target.value = ''
                        }
                      }}
                    >
                      <option value="">+ Add Tag</option>
                      {tags
                        .filter(tag => !(prospect.tags || []).includes(tag))
                        .map(tag => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                    </select>
                  </td>
                  <td title={formatDate(prospect.createdAt)}>
                    {formatDate(prospect.createdAt)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleEditProspect(prospect)
                        }}
                        title="Edit prospect"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={(e) => handleDeleteProspect(prospect.id, e)}
                        title="Delete prospect"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm.show && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <h3>⚠️ Delete Prospect</h3>
            <p>Are you sure you want to permanently delete <strong>{deleteConfirm.prospectName}</strong>?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
              >
                Delete Prospect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Prospect Form Component
const ProspectForm = ({ onSubmit, onCancel, tags, initialData = null }) => {
  // Determine if source is "Other" (not in standard list)
  const isOtherSource = initialData?.source && !['LinkedIn', 'Website', 'Conference', 'Referral'].includes(initialData.source)
  
  const industries = [
    'Technology',
    'Finance',
    'Healthcare',
    'Government',
    'Fintech',
    'SaaS',
    'Banking',
    'Insurance',
    'Manufacturing',
    'Retail',
    'Education',
    'Energy',
    'Telecommunications',
    'Real Estate',
    'Transportation',
    'Hospitality',
    'Media & Entertainment',
    'Consulting',
    'Legal',
    'Pharmaceuticals',
    'Biotechnology',
    'Aerospace',
    'Automotive',
    'Construction',
    'Agriculture',
    'Food & Beverage',
    'Fashion & Apparel',
    'Non-profit',
    'Security',
    'Cybersecurity',
    'AI/ML',
    'Cloud Services',
    'E-commerce',
    'Logistics',
    'Travel & Tourism'
  ]
  
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    role: initialData?.role || '',
    company: initialData?.company || '',
    country: initialData?.country || '',
    source: isOtherSource ? 'Other' : (initialData?.source || 'LinkedIn'),
    customSource: isOtherSource ? initialData.source : '',
    linkedinUrl: initialData?.linkedinUrl || '',
    companySize: initialData?.companySize || '',
    industry: initialData?.industry || '',
    tags: initialData?.tags || []
  })
  const [tagInput, setTagInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [industryFilter, setIndustryFilter] = useState(initialData?.industry || '')
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const submitData = {
        ...formData,
        source: formData.source === 'Other' ? formData.customSource : formData.source
      }
      delete submitData.customSource
      await onSubmit(submitData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleIndustryChange = (e) => {
    const value = e.target.value
    setIndustryFilter(value)
    setFormData({
      ...formData,
      industry: value
    })
    setShowIndustryDropdown(true)
  }

  const handleIndustrySelect = (industry) => {
    setFormData({
      ...formData,
      industry: industry
    })
    setIndustryFilter(industry)
    setShowIndustryDropdown(false)
  }

  const filteredIndustries = industries.filter(industry =>
    industry.toLowerCase().includes(industryFilter.toLowerCase())
  )

  const handleAddTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tag]
      })
    }
    setTagInput('')
  }

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    })
  }

  return (
    <div className="form-overlay">
      <div className="form-modal">
        <div className="form-header">
          <h3>{initialData ? 'Edit Prospect' : 'New Prospect'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="John Doe"
            />
          </div>

          <div className="form-group">
            <label htmlFor="role">Role *</label>
            <input
              type="text"
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              placeholder="Head of AI Risk"
            />
          </div>

          <div className="form-group">
            <label htmlFor="company">Company *</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              placeholder="Acme Inc."
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">Country *</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              placeholder="United States"
            />
          </div>

          <div className="form-group">
            <label htmlFor="source">Source *</label>
            <select
              id="source"
              name="source"
              value={formData.source}
              onChange={handleChange}
              required
            >
              <option value="LinkedIn">LinkedIn</option>
              <option value="Website">Website</option>
              <option value="Conference">Conference</option>
              <option value="Referral">Referral</option>
              <option value="Other">Other</option>
            </select>
            {formData.source === 'Other' && (
              <input
                type="text"
                id="customSource"
                name="customSource"
                value={formData.customSource}
                onChange={handleChange}
                required
                placeholder="Enter source name"
                style={{ marginTop: '0.5rem' }}
              />
            )}
          </div>

          <div className="form-group">
            <label htmlFor="linkedinUrl">LinkedIn URL</label>
            <input
              type="url"
              id="linkedinUrl"
              name="linkedinUrl"
              value={formData.linkedinUrl}
              onChange={handleChange}
              placeholder="https://www.linkedin.com/in/johndoe"
            />
            <p className="form-help-text">Optional - only if source is LinkedIn</p>
          </div>

          <div className="form-group">
            <label htmlFor="companySize">Company Size</label>
            <input
              type="text"
              id="companySize"
              name="companySize"
              value={formData.companySize}
              onChange={handleChange}
              placeholder="1-10, 11-50, 51-200, 201-500, 501-1000, 1001-5000, 5001-10000, 10000+"
            />
          </div>

          <div className="form-group">
            <label htmlFor="industry">Industry</label>
            <div className="searchable-dropdown">
              <input
                type="text"
                id="industry"
                name="industry"
                value={industryFilter}
                onChange={handleIndustryChange}
                onFocus={() => setShowIndustryDropdown(true)}
                onBlur={() => {
                  // Delay to allow click on dropdown item
                  setTimeout(() => setShowIndustryDropdown(false), 200)
                }}
                placeholder="Type to search industries..."
                autoComplete="off"
              />
              {showIndustryDropdown && filteredIndustries.length > 0 && (
                <div className="dropdown-list">
                  {filteredIndustries.map((industry, idx) => (
                    <div
                      key={idx}
                      className="dropdown-item"
                      onClick={() => handleIndustrySelect(industry)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {industry}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <div className="tag-selector">
              <select
                value={tagInput}
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddTag(e.target.value)
                  }
                }}
                className="tag-select"
              >
                <option value="">Select a tag...</option>
                {tags
                  .filter(tag => !formData.tags.includes(tag))
                  .map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
              </select>
              {formData.tags.length > 0 && (
                <div className="selected-tags">
                  {formData.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      {tag}
                      <button
                        type="button"
                        className="tag-remove"
                        onClick={() => handleRemoveTag(tag)}
                        title="Remove tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <p className="form-help-text">Add tags to filter prospects by product or category</p>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (initialData ? 'Update Prospect' : 'Create Prospect')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProspectResearch
